import React from 'react';
import PropTypes from 'prop-types';

/**
 * Card Component - Design System
 * 
 * A reusable card container component with multiple variants and padding options.
 * Follows the dark theme design system with consistent styling.
 * 
 * @component
 * @example
 * // Default card
 * <Card>
 *   <h2>Card Title</h2>
 *   <p>Card content goes here</p>
 * </Card>
 * 
 * // Elevated card with hover effect
 * <Card variant="elevated" hoverable>
 *   <ProductInfo />
 * </Card>
 */
const Card = ({
  variant = 'default',
  padding = 'md',
  hoverable = false,
  children,
  className = '',
  ...props
}) => {
  // Base styles - applied to all cards (dual-theme with JOHN JACOBS aesthetic)
  const baseStyles = `
    rounded-card
    transition-all duration-slow ease-in-out
  `;

  // Variant styles with dual-theme support
  const variantStyles = {
    default: `
      bg-light-secondary dark:bg-brand-surface
      border border-light-border-default dark:border-brand-border-default
    `,
    elevated: `
      bg-white dark:bg-brand-elevated
      shadow-[0px_4px_12px_rgba(0,0,0,0.06)]
    `,
    outlined: `
      bg-transparent
      border-2 border-light-border-default dark:border-brand-border-default
    `,
  };

  // Padding styles (JOHN JACOBS: minimum 16px)
  const paddingStyles = {
    none: '',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
  };

  // Hoverable styles with dual-theme support
  const hoverStyles = hoverable
    ? `
      hover:shadow-lg
      hover:scale-[1.02]
      hover:border-light-border-hover dark:hover:border-brand-border-hover
      cursor-pointer
    `
    : '';

  return (
    <div
      className={`
        ${baseStyles}
        ${variantStyles[variant]}
        ${paddingStyles[padding]}
        ${hoverStyles}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      {...props}
    >
      {children}
    </div>
  );
};

Card.propTypes = {
  /** Card variant style */
  variant: PropTypes.oneOf(['default', 'elevated', 'outlined']),
  /** Card padding size */
  padding: PropTypes.oneOf(['none', 'sm', 'md', 'lg']),
  /** Enable hover effects (scale and shadow) */
  hoverable: PropTypes.bool,
  /** Card content */
  children: PropTypes.node.isRequired,
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default Card;
